import taskmanAxios from '../helpers/axios'
import {
    API_MAKE_PROJECT_DOCUMENTS_URL,
    API_MAKE_WORKSPACE_MEMBERS_GET_URL,
    API_MAKE_WORKSPACE_MEMBERS_PHOTOS_URL,
    API_PROJECT_LIST_URL,
    API_PROJECT_WORK_SPACE_TASK_URL,
    API_SEARCH_URL,
} from '../constants/api.constant'
import type {
    GlobalSearchEntityType,
    GlobalSearchResponse,
    GlobalSearchResult,
    GlobalSearchTaskColumn,
    GlobalSearchTaskUser,
} from 'cor-business'
import {
    LIST_VIEW_COLUMN_ORDER,
    readProjectListViewColumns,
} from '../page/Project/utils/listViewColumns'
import { normalizeTaskDate } from '../utils/formatDate'
import timeToDHMFormat from '../utils/time'
import {getTaskSpentHours} from '../utils/taskSpentTime'

interface TaskManagerSearchParams {
    query: string
    workspaceId?: number
    currentUserIds?: Array<string | number>
    currentUserEmails?: string[]
    types: GlobalSearchEntityType[]
    limit: number
    signal?: AbortSignal
}

const groupKeys: Record<GlobalSearchEntityType, string[]> = {
    project: ['projects'],
    task: ['tasks'],
    user: ['users', 'participants', 'members', 'photos'],
    document: ['documents'],
}

const apiFilters: Record<GlobalSearchEntityType, string> = {
    project: 'projects',
    task: 'tasks',
    user: 'members',
    document: 'documents',
}

const taskPriorities: Record<string, { label: string; color: string }> = {
    urgent: { label: 'Срочный', color: '#E62F2F' },
    high: { label: 'Высокий', color: '#E78D07' },
    medium: { label: 'Средний', color: '#458CF6' },
    low: { label: 'Низкий', color: '#7E8592' },
}

const taskTypeLabels: Record<string, string> = {
    task: 'Задача',
    bug: 'Ошибка',
    meeting: 'Встреча',
    document: 'Документ',
    approval_review: 'Согласование/проверка',
}

function extractGroup(data: any, type: GlobalSearchEntityType) {
    if (Array.isArray(data)) {
        return { items: data, total: data.length }
    }

    const key = groupKeys[type].find((candidate) => data?.[candidate] != null || data?.results?.[candidate] != null)
    const group = key ? (data?.[key] ?? data?.results?.[key]) : undefined

    if (Array.isArray(group)) {
        return { items: group, total: group.length }
    }

    const fallbackItems = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data?.data)
            ? data.data
            : []
    const items = group?.items || group?.results || group?.data || fallbackItems
    return {
        items: Array.isArray(items) ? items : [],
        total: Number(group?.total ?? group?.count ?? items.length ?? 0),
    }
}

function getUserName(user: any) {
    const profile = user?.profile || user?.user?.profile || {}
    const source = user?.user || user || {}

    return source.full_name
        || source.name
        || [source.first_name || profile.first_name, source.last_name || profile.last_name].filter(Boolean).join(' ')
        || source.email
        || 'Участник'
}

function getUserAvatarUrl(user: any) {
    const source = user?.user || user || {}
    const profile = source?.profile || user?.profile || {}
    const candidates = [
        source?.photo_data_url,
        user?.photo_data_url,
        profile?.photo_data_url,
        source?.avatar_url,
        source?.avatar,
        source?.photo_url,
        source?.photo,
        profile?.avatar_url,
        profile?.avatar,
        profile?.photo_url,
        profile?.photo,
    ]

    return candidates.find((value) => (
        typeof value === 'string'
        && value.trim() !== ''
        && value !== 'null'
        && value !== 'undefined'
    ))?.trim()
}

export function buildParticipantHref(
    workspaceId: string | number,
    userId: string | number,
    query?: string,
) {
    const params = new URLSearchParams({ user: String(userId) })
    const trimmedQuery = query?.trim()

    if (trimmedQuery) {
        params.set('query', trimmedQuery)
    }

    return `/participants/${workspaceId}?${params.toString()}`
}

export function buildGlobalParticipantHref(
    userId: string | number,
    query?: string,
    email?: string,
) {
    const params = new URLSearchParams()
    if (query?.trim()) params.set('query', query.trim())
    if (email?.trim()) params.set('email', email.trim())
    const search = params.toString()
    return `/participants/user/${encodeURIComponent(String(userId))}${search ? `?${search}` : ''}`
}

function mapTaskUser(user: any): GlobalSearchTaskUser {
    const source = user?.user || user || {}

    return {
        id: source.id ?? source.user_id,
        name: getUserName(user),
        avatarUrl: getUserAvatarUrl(user),
    }
}

function getUserIds(user: any) {
    return [
        user?.id,
        user?.user_id,
        user?.user?.id,
        user?.user?.user_id,
        user?.profile?.id,
        user?.profile?.user_id,
    ].filter((value) => value != null).map((value) => String(value))
}

function isCurrentUserTask(
    item: any,
    currentUserIds: Array<string | number> = [],
    currentUserEmails: string[] = []
) {
    const expectedIds = new Set(currentUserIds.map((value) => String(value)))
    const expectedEmails = new Set(currentUserEmails.map((value) => value.toLowerCase()))

    if (expectedIds.size === 0 && expectedEmails.size === 0) return false

    const relatedUsers = [
        item?.created_by,
        item?.creator,
        item?.author,
        item?.owner,
        item?.responsible,
        item?.assignee,
        ...(Array.isArray(item?.assignees) ? item.assignees : []),
        ...(Array.isArray(item?.users) ? item.users : []),
        ...(Array.isArray(item?.assignee_ids)
            ? item.assignee_ids.map((id: string | number) => ({ id }))
            : []),
    ]
    const directIds = [
        item?.owner_id,
        item?.user_id,
        item?.responsible_id,
        item?.assigned_to,
        item?.created_by_id,
        item?.creator_id,
        item?.author_id,
    ].filter((value) => value != null && typeof value !== 'object').map((value) => String(value))
    const userIds = [...directIds, ...relatedUsers.flatMap(getUserIds)]

    if (userIds.some((id) => expectedIds.has(id))) return true

    return relatedUsers.some((user) => {
        const source = user?.user || user?.profile || user || {}
        const email = source?.email || user?.email
        return email && expectedEmails.has(String(email).toLowerCase())
    })
}

function prioritize<T>(items: T[], predicate: (item: T) => boolean) {
    return items
        .map((item, index) => ({ item, index, priority: predicate(item) ? 0 : 1 }))
        .sort((left, right) => left.priority - right.priority || left.index - right.index)
        .map(({ item }) => item)
}

function getTaskVisibleColumns(projectId?: string | number): GlobalSearchTaskColumn[] {
    const state = readProjectListViewColumns(projectId)

    return LIST_VIEW_COLUMN_ORDER.filter((key) => (
        key !== 'title'
        && key !== 'status'
        && key !== 'totalTimeSpent'
        && state[key]
    )) as GlobalSearchTaskColumn[]
}

function getTaskTimeTracking(item: any) {
    const hours = getTaskSpentHours(item)

    if (hours == null || hours <= 0) return undefined
    return timeToDHMFormat(hours)
}

export function mapResult(
    type: GlobalSearchEntityType,
    item: any,
    workspaceId?: number,
    query?: string,
): GlobalSearchResult {
    const projectId = item?.project_id ?? item?.project?.id
    const userId = item?.user_id ?? item?.user?.id ?? item?.id
    const itemWorkspaceId = item?.workspace_id
        ?? item?.space_id
        ?? item?.workspaceId
        ?? item?.spaceId
        ?? item?.workspace?.id
        ?? item?.space?.id
        ?? item?.project?.workspace_id
        ?? item?.project?.space_id
        ?? item?.project?.workspaceId
        ?? item?.project?.spaceId
        ?? item?.project?.workspace?.id
        ?? item?.project?.space?.id

    if (type === 'project') {
        return {
            id: item.id,
            type,
            title: item.name || item.title || `Проект #${item.id}`,
            workspaceId: itemWorkspaceId,
            subtitle: item.workspace_name || item.workspace?.name,
            date: item.created_at,
            color: item.color,
            href: `/projects/${item.id}`,
        }
    }

    if (type === 'task') {
        const priorityKey = String(item?.priority || '').toLowerCase()
        const priority = taskPriorities[priorityKey]
        const createdAt = normalizeTaskDate(item?.created_at ?? item?.date_created) || undefined
        const assignees = Array.isArray(item?.assignees)
            ? item.assignees.map(mapTaskUser)
            : Array.isArray(item?.users)
                ? item.users.map(mapTaskUser)
                : []
        const creatorSource = item?.creator || item?.created_by || item?.author
        const projectName = item?.project_name || item?.project?.name || item?.project?.title

        return {
            id: item.id,
            type,
            title: item.title || item.name || `Задача #${item.id}`,
            workspaceId: itemWorkspaceId,
            subtitle: item.project_name || item.project?.name,
            date: createdAt,
            counters: {
                attachments: Number(item?.attachments_count ?? 0),
                comments: Number(item?.comments_count ?? 0),
                subtasks: Number(item?.subtasks_count ?? 0),
            },
            taskDetails: {
                visibleColumns: getTaskVisibleColumns(projectId),
                dueDate: item?.due_date,
                priority: priority || (item?.priority
                    ? { label: String(item.priority), color: '#7E8592' }
                    : undefined),
                users: assignees,
                project: projectId != null || projectName
                    ? { id: projectId, name: projectName || `Project #${projectId}` }
                    : undefined,
                taskType: taskTypeLabels[item?.task_type] || item?.task_type || 'Задача',
                createdAt,
                creator: creatorSource ? mapTaskUser(creatorSource) : undefined,
                timeTracking: getTaskTimeTracking(item),
            },
            href: projectId == null ? undefined : `/projects/${projectId}?view=list&task=${item.id}`,
        }
    }

    if (type === 'user') {
        const source = item?.user || item
        const userWorkspaceId = itemWorkspaceId ?? workspaceId
        const email = source?.email || item?.email
        const isPendingInvitation = [item?.is_pending_invitation, item?.pending_registration]
            .some((flag) => flag === true || String(flag).toLowerCase() === 'true')
        return {
            id: userId,
            type,
            title: getUserName(item),
            workspaceId: userWorkspaceId,
            subtitle: email,
            avatarUrl: getUserAvatarUrl(item),
            href: isPendingInvitation && userWorkspaceId != null
                ? buildParticipantHref(userWorkspaceId, userId, query)
                : buildGlobalParticipantHref(userId, query, email),
        }
    }

    const documentName = item.name || item.title || item.filename || item.file_name || item.original_filename
    const documentOwner = item?.owner || item?.created_by || item?.author || item?.uploaded_by
    return {
        id: item.id,
        type,
        title: documentName || `Документ #${item.id}`,
        workspaceId: itemWorkspaceId,
        subtitle: item.project_name || item.project?.name,
        date: item.created_at || item.uploaded_at,
        documentDetails: {
            owner: documentOwner ? mapTaskUser(documentOwner) : undefined,
            projectId,
            isArchived: Boolean(item?.is_archived || item?.archived_at || item?.status === 'archived'),
        },
        href: projectId == null ? undefined : `/projects/${projectId}?view=documents&document=${item.id}&action=open`,
    }
}

interface SearchScope {
    projects: any[]
    projectIds: Set<string>
    workspaceMembers: any[]
    workspaceMemberIds: Set<string>
    workspacePhotoByUserId: Map<string, string>
}

let searchScopeCache: {
    workspaceId?: number
    expiresAt: number
    promise: Promise<SearchScope>
} | null = null

function getSearchScope(workspaceId?: number): Promise<SearchScope> {
    if (
        searchScopeCache
        && searchScopeCache.workspaceId === workspaceId
        && searchScopeCache.expiresAt > Date.now()
    ) {
        return searchScopeCache.promise
    }

    const promise = Promise.all([
        taskmanAxios.get(API_PROJECT_LIST_URL, {
            params: {
                skip: 0,
                limit: 100,
                mine_only: false,
                archived_only: false,
                trash_only: false,
            },
        }).catch(() => null),
        workspaceId != null
            ? taskmanAxios.get(API_MAKE_WORKSPACE_MEMBERS_GET_URL(workspaceId), {
                params: { skip: 0, limit: 1000 },
            }).catch(() => null)
            : Promise.resolve(null),
        workspaceId != null
            ? taskmanAxios.get(API_MAKE_WORKSPACE_MEMBERS_PHOTOS_URL(workspaceId), {
                params: { skip: 0, limit: 1000 },
            }).catch(() => null)
            : Promise.resolve(null),
    ]).then(([projectsResponse, membersResponse, photosResponse]) => {
        const projects = extractGroup(projectsResponse?.data, 'project').items
        const workspacePhotos = extractGroup(photosResponse?.data, 'user').items
        const workspacePhotoByUserId = new Map<string, string>()

        workspacePhotos.forEach((photo: any) => {
            const photoDataUrl = getUserAvatarUrl(photo)
            if (!photoDataUrl) return

            getUserIds(photo).forEach((id) => workspacePhotoByUserId.set(id, photoDataUrl))
        })

        const workspaceMembers = extractGroup(membersResponse?.data, 'user').items.map((member: any) => {
            const photoDataUrl = getUserIds(member)
                .map((id) => workspacePhotoByUserId.get(id))
                .find(Boolean)

            return photoDataUrl ? { ...member, photo_data_url: photoDataUrl } : member
        })

        return {
            projects,
            projectIds: new Set(projects.map((project: any) => String(project.id))),
            workspaceMembers,
            workspaceMemberIds: new Set(workspaceMembers.flatMap(getUserIds)),
            workspacePhotoByUserId,
        }
    })

    searchScopeCache = {
        workspaceId,
        expiresAt: Date.now() + 30_000,
        promise,
    }

    return promise
}

async function loadInitialResults({
    workspaceId,
    currentUserIds,
    currentUserEmails,
    types,
    limit,
    signal,
}: Omit<TaskManagerSearchParams, 'query'>): Promise<GlobalSearchResponse> {
    const requestLimit = Math.min(limit + 1, 50)
    const optionalGet = async (url: string, config: Record<string, any>) => {
        try {
            return await taskmanAxios.get(url, config)
        } catch (error) {
            if (signal?.aborted) throw error
            return null
        }
    }

    const scope = await getSearchScope(workspaceId)
    const taskWorkspaceIds = Array.from(new Set([
        workspaceId,
        ...scope.projects.map((project: any) => project?.workspace_id ?? project?.workspace?.id),
    ].filter((value) => value != null)))
    const taskResponses = types.includes('task')
        ? await Promise.all(taskWorkspaceIds.map((taskWorkspaceId) => (
            optionalGet(API_PROJECT_WORK_SPACE_TASK_URL, {
                params: {
                    workspace_id: taskWorkspaceId,
                    skip: 0,
                    limit: 100,
                },
                signal,
            })
        )))
        : []

    const projectGroup = {
        items: scope.projects,
        total: scope.projects.length,
    }
    const taskMap = new Map<string, any>()
    taskResponses
        .flatMap((response) => extractGroup(response?.data, 'task').items)
        .forEach((task: any) => {
            if (task?.id != null) {
                taskMap.set(String(task.id), task)
            }
        })
    const initialTasks = Array.from(taskMap.values())
    const taskGroup = {
        items: initialTasks,
        total: initialTasks.length,
    }
    const memberGroup = {
        items: scope.workspaceMembers,
        total: scope.workspaceMembers.length,
    }
    const documentItems: any[] = []

    if (types.includes('document')) {
        const currentWorkspaceProjects = scope.projects.filter((project: any) => (
            workspaceId != null
            && String(project?.workspace_id ?? project?.workspace?.id) === String(workspaceId)
        ))

        for (const project of currentWorkspaceProjects.slice(0, 5)) {
            if (documentItems.length >= requestLimit) break

            try {
                const response = await taskmanAxios.get(API_MAKE_PROJECT_DOCUMENTS_URL(project.id), {
                    params: { skip: 0, limit: requestLimit - documentItems.length },
                    signal,
                })
                const documents = extractGroup(response.data, 'document').items

                documentItems.push(...documents.map((document: any) => ({
                    ...document,
                    project_id: document.project_id ?? project.id,
                    project: document.project ?? {
                        id: project.id,
                        name: project.name || project.title,
                    },
                })))
            } catch (error) {
                if (signal?.aborted) throw error
            }
        }
    }

    const sourceGroups: Record<GlobalSearchEntityType, { items: any[]; total: number }> = {
        project: projectGroup,
        task: taskGroup,
        user: memberGroup,
        document: { items: documentItems, total: documentItems.length },
    }
    const totals: GlobalSearchResponse['totals'] = {}
    const results = types.flatMap((type) => {
        const group = sourceGroups[type]
        totals[type] = group.total

        return group.items
            .slice(0, limit)
            .map((item) => mapResult(type, item, workspaceId))
    })

    return { results, totals }
}

export default class SearchService {
    static async search({
        query,
        workspaceId,
        currentUserIds,
        currentUserEmails,
        types,
        limit,
        signal,
    }: TaskManagerSearchParams): Promise<GlobalSearchResponse> {
        if (!query.trim()) {
            return loadInitialResults({
                workspaceId,
                currentUserIds,
                currentUserEmails,
                types,
                limit,
                signal,
            })
        }

        const [response, scope] = await Promise.all([
            taskmanAxios.get(API_SEARCH_URL, {
                params: {
                    q: query,
                    filter: types.length === 1 ? apiFilters[types[0]] : 'all',
                    // Берём один дополнительный элемент, чтобы показать
                    // «Смотреть больше», не запрашивая отдельный total.
                    limit_per_type: Math.min(limit + 1, 50),
                },
                signal,
            }),
            getSearchScope(workspaceId),
        ])
        const data = response.data || {}
        const prepareItems = (type: GlobalSearchEntityType, items: any[]) => {
            if (type === 'project') {
                return items.filter((item) => scope.projectIds.has(String(item.id)))
            }

            if (type === 'task') {
                return prioritize(
                    items,
                    (item) => isCurrentUserTask(item, currentUserIds, currentUserEmails)
                )
            }

            if (type === 'user') {
                const itemsWithPhotos = items.map((item) => {
                    const photoDataUrl = getUserIds(item)
                        .map((id) => scope.workspacePhotoByUserId.get(id))
                        .find(Boolean)

                    return photoDataUrl ? { ...item, photo_data_url: photoDataUrl } : item
                })

                return prioritize(itemsWithPhotos, (item) => (
                    getUserIds(item).some((id) => scope.workspaceMemberIds.has(id))
                ))
            }

            return items
        }

        if (Array.isArray(data.results)) {
            const totals: GlobalSearchResponse['totals'] = {}
            const results = types.flatMap((type) => {
                const items = prepareItems(
                    type,
                    data.results.filter((item: any) => item.type === type)
                )
                totals[type] = items.length

                return items
                    .slice(0, limit)
                    .map((item: any) => mapResult(type, item, workspaceId, query))
            })

            return { results, totals }
        }

        const totals: GlobalSearchResponse['totals'] = {}
        const results = types.flatMap((type) => {
            const group = extractGroup(data, type)
            const items = prepareItems(type, group.items)
            totals[type] = type === 'project' ? items.length : group.total

            return items
                .slice(0, limit)
                .map((item: any) => mapResult(type, item, workspaceId, query))
        })

        return { results, totals }
    }
}
